import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TextField, Button, Box, Grid, IconButton, InputAdornment, Avatar, Typography } from '@mui/material';
import { LockOutlined as LockOutlinedIcon } from "@mui/icons-material";
import { CheckCircleOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '../../../redux/reducer/dialogSlice';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import GlobalDialogContent from '../GlobalDialogContent';
import { ChangeUserPasswordService } from '../../../utils/services/auth.service';
import { useNavigate } from 'react-router-dom';
import { encryptRSA } from '../../../utils/services/rsaEncrypt.service';
import ReCaptchaComponent from '../../../pages/Auth/ReCaptcha';
import { RootState } from '../../../redux/store';
import PageLang from '../../../config/page.languge';

interface ChangePasswordForm {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const ChangePasswordDialog: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [captcha, setCaptcha] = useState<any>();
  const [resetCaptcha, resetCaptch] = useState<boolean>(true);
  const lang=useSelector((state:RootState)=>state.lang.language)

  const validationSchema = Yup.object({
    old_password: Yup.string()
      .min(8, PageLang.loginOptions[6][lang] )
      .required( PageLang.loginOptions[7][lang] ),
    new_password: Yup.string()
      .min(8, PageLang.loginOptions[8][lang] )
      .required(PageLang.loginOptions[9][lang] ).matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        PageLang.loginOptions[10][lang]
      ),
    confirm_password: Yup.string().required(PageLang.loginOptions[11][lang])
      .oneOf([Yup.ref('new_password')], PageLang.loginOptions[12][lang])
      
  });

  const formik = useFormik<ChangePasswordForm>({
    initialValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!captcha.captchaValue) {
        dispatch(showSnackbar({ type: 'error', message: PageLang?.loginOptions[15][lang] }));
        return
      }
      const EncurrentPassword:string = await encryptRSA(values.old_password);  
      const EnNewPassword:string = await encryptRSA(values.new_password);  
      const EnConfirmPassword:string = await encryptRSA(values.confirm_password); 

      const payloadData = {
        current_password: EncurrentPassword,
        new_password: EnNewPassword,
        confirm_password: EnConfirmPassword,
        captcha: captcha.captchaValue, 
        ref: captcha.imageSrc.uuid,
      };
      
      resetCaptch(false);
      const { code, message } = await ChangeUserPasswordService(payloadData);
      if (code === 200) {
        dispatch(showSnackbar({ type: 'success', message: message || 'Password changed successfully' }));
        dispatch(closeDialog());
        navigate('auth/logout')
      } else {
        dispatch(showSnackbar({ type: 'error', message: message || 'Failed to change password' }));
      }
      resetCaptch(true);
    },
  });

  const onCaptchaValue = (captcha: any) => {
    setCaptcha(captcha);
  };

  return (
    <Box>
      <GlobalDialogContent
        dialogBody={
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "primary.main", width: 54, height: 54, mb: 1 }}>
                <LockOutlinedIcon fontSize="large" />
              </Avatar>
              <Typography variant="h5" fontWeight={700}>
              {PageLang.loginOptions[1][lang] }
              </Typography>
            </Box>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type={showOldPassword ? 'text' : 'password'}
                  label={<>{PageLang.loginOptions[2][lang]}<span style={{ color: 'red' }}>*</span></>}
                  name="old_password"
                  value={formik.values.old_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.old_password && Boolean(formik.errors.old_password)}
                  helperText={formik.touched.old_password && formik.errors.old_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
                    onCopy: (e: React.ClipboardEvent) => e.preventDefault(),
                    style: { userSelect: 'none' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type={showNewPassword ? 'text' : 'password'}
                  label={<>{PageLang.loginOptions[3][lang]} <span style={{ color: 'red' }}>*</span></>}
                  name="new_password"
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.new_password && Boolean(formik.errors.new_password)}
                  helperText={formik.touched.new_password && formik.errors.new_password}
                  inputProps={{
                    onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
                    onCopy: (e: React.ClipboardEvent) => e.preventDefault(),
                    style: { userSelect: 'none' },
                  }}         
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type={'password'}
                  label={<>{PageLang.loginOptions[4][lang]}<span style={{ color: 'red' }}>*</span></>}
                  name="confirm_password"
                  value={formik.values.confirm_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                  helperText={formik.touched.confirm_password && formik.errors.confirm_password}      
                  inputProps={{
                    onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
                    onCopy: (e: React.ClipboardEvent) => e.preventDefault(),
                    style: { userSelect: 'none' },
                  }}
                />
              </Grid>
              <ReCaptchaComponent onCaptchaValue={onCaptchaValue} resetCaptch={resetCaptcha}/>

            </Grid>
          </form>
        }
        dialogFooter={
          <Button
            onClick={() => formik.handleSubmit()}
            type="submit"
            variant="contained"
            color="success"
            endIcon={<CheckCircleOutline />}
            disabled={formik.isSubmitting}
          >
            {PageLang.loginOptions[1][lang] }
          </Button>
        }
      />
    </Box>
  );
};
export default ChangePasswordDialog;